/**
 * The key that makes a share link openable, and never reaches this server.
 *
 * A visitor following a share link has no account, so no group key, so nothing
 * to decrypt an end-to-end encrypted photograph with. Sharing was simply
 * refused for sealed files, which by now is every file.
 *
 * The way out is the one the URL specification hands you for free: everything
 * after `#` is fragment, and browsers do not put fragments in requests. So the
 * creator's browser mints a random key, re-wraps each file key under it, and
 * hands the server the wrapped copies; the key itself is written after the `#`
 * and travels only inside whatever message the link was pasted into.
 *
 * What that buys, precisely: the server stores a token it can match and a pile
 * of ciphertext it cannot open. What it does not buy: protection from whoever
 * holds the link. A share link *is* the key — that is what sharing means here —
 * so anyone it is forwarded to can read the photographs, which is why revoking
 * exists and why the manager shows view counts.
 */

import {
  bytesToBase64,
  base64ToBytes,
  generateSymmetricKey,
  unwrapWithKey,
  wrapWithKey,
} from "@fc/core";

/** The fragment parameter carrying the key. Short, because links get typed. */
const FRAGMENT_KEY = "k";

/** A fresh 256-bit key for one link. Never stored; never sent to the server. */
export function newLinkKey(): Uint8Array {
  return generateSymmetricKey();
}

/**
 * URL-safe base64, because this lives in a URL.
 *
 * Standard base64's `+` and `/` survive a fragment, but not a link pasted into
 * software that helpfully "fixes" URLs, and `=` padding invites truncation. The
 * transformation is reversed exactly on the way back in.
 */
export function encodeLinkKey(key: Uint8Array): string {
  return bytesToBase64(key)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function decodeLinkKey(encoded: string): Uint8Array {
  const padded = encoded.replace(/-/g, "+").replace(/_/g, "/");
  return base64ToBytes(padded + "=".repeat((4 - (padded.length % 4)) % 4));
}

/** Build the shareable address: the server's URL, plus the key nobody sends. */
export function withLinkKey(url: string, key: Uint8Array): string {
  return `${url}#${FRAGMENT_KEY}=${encodeLinkKey(key)}`;
}

/**
 * The link key from this page's own address, or null if there is none.
 *
 * Null is a real state, not an error: a link made before this existed, or one
 * forwarded by somebody whose chat client stripped the fragment. The viewer
 * says so rather than showing broken pictures.
 */
export function linkKeyFromLocation(hash: string): Uint8Array | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const encoded = params.get(FRAGMENT_KEY);
  if (!encoded) return null;

  try {
    const key = decodeLinkKey(encoded);
    /* A 256-bit key or nothing — a short one is a corrupted link, not a key. */
    return key.length === 32 ? key : null;
  } catch {
    return null;
  }
}

/** Re-wrap a file key under the link key, for the server to hold. */
export function sealForLink(
  fileKey: Uint8Array,
  linkKey: Uint8Array,
): Promise<string> {
  return wrapWithKey(fileKey, linkKey);
}

/** Open one, on the visitor's side. */
export function openWithLink(
  sealed: string,
  linkKey: Uint8Array,
): Promise<Uint8Array> {
  return unwrapWithKey(sealed, linkKey);
}
